import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchVideoProject, ScriptResponse } from '@/api';
import ScriptGenerationModal from '@/components/ScriptGenerationModal';
import ScriptDisplay from '@/components/ScriptDisplay';
import { VideoUploader } from '@/components/upload/VideoUploader';

interface VideoProject {
  id: string;
  title: string;
  description: string;
  status: 'draft' | 'scripting' | 'production' | 'review' | 'published' | 'uploading' | 'uploaded' | 'transcribing';
  content_type: string;
  created_at: string;
  updated_at: string;
  script?: string;
  script_content?: string;
  script_metadata?: string;
  thumbnail_url?: string;
  youtube_video_id?: string;
}

export default function VideoProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<VideoProject | null>(null);
  const [script, setScript] = useState<ScriptResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  useEffect(() => {
    if (id) {
      loadProject();
    }
  }, [id]);

  const loadProject = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const projectData = await fetchVideoProject(id);
      setProject(projectData);

      // Parse existing script if available (API returns it as 'script' field)
      const scriptField = projectData.script || projectData.script_content;
      if (scriptField) {
        try {
          let scriptData = typeof scriptField === 'string'
            ? JSON.parse(scriptField)
            : scriptField;

          // If script is flat (just sections), reconstruct full ScriptResponse format
          if (scriptData.sections && !scriptData.metadata) {
            scriptData = {
              success: true,
              script: scriptData,
              metadata: {
                title: projectData.title,
                description: projectData.description || '',
                tags: projectData.metadata?.tags || [],
                estimatedDuration: projectData.metadata?.estimatedDuration || '3:00',
                retentionScore: projectData.metadata?.retentionScore || 85,
                retentionReasoning: projectData.metadata?.retentionReasoning || '',
                complexity: projectData.metadata?.complexity || 'Beginner'
              },
              productionNotes: {
                visualCuesHints: [],
                keyTakeaways: [],
                suggestions: []
              }
            };
          }
          setScript(scriptData);
        } catch (e) {
          console.error('Failed to parse script:', e);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const handleScriptGenerated = (newScript: ScriptResponse) => {
    setScript(newScript);
    setShowGenerateModal(false);

    // Update project status to scripting if it was draft
    if (project?.status === 'draft') {
      setProject(prev => prev ? { ...prev, status: 'scripting' } : null);
    }
  };

  const handleScriptUpdated = (updatedScript: ScriptResponse, improvement?: any) => {
    setScript(updatedScript);

    // Show improvement notification if available
    if (improvement) {
      console.log('Script improved:', improvement);
      // You could add a toast notification here
    }
  };

  const handleAcceptScript = async () => {
    if (!project || !project.id) {
      console.error('Missing project data:', { project, projectId: id });
      setError('Unable to save script - missing project data');
      return;
    }

    try {
      // Script is already auto-saved, just update status to production
      const response = await fetch(`/api/video-projects/${project.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          status: 'production'
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save script: ${response.status} ${errorText}`);
      }

      // Update project status to production
      setProject(prev => prev ? { ...prev, status: 'production' } : null);

      // Navigate to storyboard for visual planning
      navigate(`/projects/${project.id}/storyboard`);
    } catch (error) {
      console.error('Failed to accept script:', error);
      setError('Failed to accept script. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-gray-600">Loading project...</span>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Project Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'The requested project could not be loaded.'}</p>
          <button
            onClick={() => navigate('/projects')}
            className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'scripting': return 'bg-blue-100 text-blue-800';
      case 'production': return 'bg-yellow-100 text-yellow-800';
      case 'review': return 'bg-purple-100 text-purple-800';
      case 'published': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate('/projects')}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="Back to Projects"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">{project.title}</h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(project.status)}`}>
                {project.status}
              </span>
            </div>
            <p className="text-gray-600">{project.description}</p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span>📅 Created {new Date(project.created_at).toLocaleDateString()}</span>
            <span>🎬 {project.content_type}</span>
            {project.youtube_video_id && (
              <span className="text-green-600">✅ Published</span>
            )}
          </div>

          {!script && (
            <button
              onClick={() => setShowGenerateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Generate Script
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {script ? (
        <ScriptDisplay
          script={script}
          projectId={project.id}
          onScriptUpdated={handleScriptUpdated}
          onAccept={handleAcceptScript}
        />
      ) : project.status === 'review' || project.status === 'uploaded' ? (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Video Processed & Ready</h2>
          <p className="text-gray-600 mb-8 max-w-lg mx-auto">
            Your video has been analyzed. You can now review the AI-suggested edits.
          </p>
          <button
            onClick={() => navigate(`/project/${project.id}/review`)}
            className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors shadow-lg font-medium text-lg"
          >
            ✂️ Review Edits
          </button>
        </div>
      ) : (
        /* No Content State */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
          {/* Option 1: Upload Video (Primary) */}
          <div className="bg-white p-8 rounded-2xl shadow-sm border-2 border-dashed border-indigo-200 hover:border-indigo-400 transition-colors">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 text-center">Option 1: Upload Video</h2>
            <div className="mb-6">
              <VideoUploader
                projectId={project.id}
                onUploadComplete={() => {
                  // Refresh project to check status
                  loadProject();
                  // Optionally navigate or show success
                }}
              />
            </div>
            <p className="text-sm text-gray-500 text-center">
              Upload your raw footage (talking head, vlog) and let our AI edit it for you.
            </p>
          </div>

          {/* Option 2: Generate Script (Secondary) */}
          <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200 flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 shadow-sm">
              <svg className="w-8 h-8 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Option 2: Generate Script</h2>
            <p className="text-gray-600 mb-6">
              Start from scratch with an AI-written script optimized for retention.
            </p>
            <button
              onClick={() => setShowGenerateModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Draft Script
            </button>
          </div>
        </div>
      )}

      {/* Script Generation Modal */}
      {project && (
        <ScriptGenerationModal
          open={showGenerateModal}
          onClose={() => setShowGenerateModal(false)}
          onScriptGenerated={handleScriptGenerated}
          projectId={project.id}
        />
      )}
    </div>
  );
}